import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { 
  Info, Phone, HelpCircle, Shield, FileText, RefreshCw, 
  Mail, MapPin, Clock, MessageCircle
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL paths to slugs
const PATH_TO_SLUG = {
  '/about': 'about-us',
  '/about-us': 'about-us',
  '/contact': 'contact',
  '/help': 'help-faq',
  '/help-faq': 'help-faq',
  '/privacy': 'privacy-policy',
  '/privacy-policy': 'privacy-policy',
  '/terms': 'terms-of-service',
  '/terms-of-service': 'terms-of-service',
  '/refund': 'refund-policy',
  '/refund-policy': 'refund-policy'
};

const PAGE_ICONS = {
  'about-us': Info,
  'contact': Phone,
  'help-faq': HelpCircle,
  'privacy-policy': Shield,
  'terms-of-service': FileText,
  'refund-policy': RefreshCw
};

const DEFAULT_CONTENT = {
  'about-us': {
    title: 'About Nammahut',
    content: `
      <div class="space-y-6">
        <p class="text-lg">Welcome to <strong>Nammahut</strong> - Your Traditional Indian Marketplace!</p>
        
        <p>Nammahut (meaning "Our Hut" in many Indian languages) is a platform dedicated to bringing authentic Indian products directly from artisans and small businesses to your doorstep.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Our Mission</h3>
        <p>To preserve and promote traditional Indian craftsmanship by connecting skilled artisans with customers who appreciate authentic, handcrafted products.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">What We Offer</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>Handloom textiles and traditional clothing</li>
          <li>Authentic Indian sweets and snacks</li>
          <li>Premium spices and organic foods</li>
          <li>Traditional handicrafts and home décor</li>
          <li>Ayurvedic and herbal products</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Our Promise</h3>
        <p>Every product on Nammahut is sourced directly from verified vendors, ensuring authenticity, quality, and fair pricing. We support local artisans and help preserve India's rich cultural heritage.</p>
      </div>
    `
  },
  'contact': {
    title: 'Contact Us',
    content: `
      <div class="space-y-6">
        <p class="text-lg">We'd love to hear from you! Get in touch with us through any of the following channels.</p>
        
        <div class="grid md:grid-cols-2 gap-6 mt-8">
          <div class="bg-green-50 p-6 rounded-xl border border-green-200">
            <h3 class="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
              📧 Email Support
            </h3>
            <p class="text-gray-700">support@nammahut.com</p>
            <p class="text-sm text-gray-500 mt-2">We respond within 24 hours</p>
          </div>
          
          <div class="bg-green-50 p-6 rounded-xl border border-green-200">
            <h3 class="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
              📞 Phone Support
            </h3>
            <p class="text-gray-700">+91 800 123 4567</p>
            <p class="text-sm text-gray-500 mt-2">Mon-Sat, 9 AM - 6 PM IST</p>
          </div>
          
          <div class="bg-green-50 p-6 rounded-xl border border-green-200">
            <h3 class="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
              💬 WhatsApp
            </h3>
            <p class="text-gray-700">+91 800 123 4567</p>
            <p class="text-sm text-gray-500 mt-2">Quick responses via WhatsApp</p>
          </div>
          
          <div class="bg-green-50 p-6 rounded-xl border border-green-200">
            <h3 class="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
              📍 Office Address
            </h3>
            <p class="text-gray-700">Chennai, Tamil Nadu, India</p>
            <p class="text-sm text-gray-500 mt-2">Visit by appointment only</p>
          </div>
        </div>
      </div>
    `
  },
  'help-faq': {
    title: 'Help & FAQ',
    content: `
      <div class="space-y-6">
        <p class="text-lg">Find answers to frequently asked questions below.</p>
        
        <div class="space-y-4 mt-6">
          <details class="bg-white border border-green-200 rounded-lg p-4 group">
            <summary class="font-semibold text-green-800 cursor-pointer">How do I place an order?</summary>
            <p class="mt-3 text-gray-700">Browse our products, add items to your cart, and proceed to checkout. You can pay using UPI, cards, or net banking.</p>
          </details>
          
          <details class="bg-white border border-green-200 rounded-lg p-4 group">
            <summary class="font-semibold text-green-800 cursor-pointer">What payment methods are accepted?</summary>
            <p class="mt-3 text-gray-700">We accept UPI, credit/debit cards, net banking, and popular wallets through our secure payment partner Cashfree.</p>
          </details>
          
          <details class="bg-white border border-green-200 rounded-lg p-4 group">
            <summary class="font-semibold text-green-800 cursor-pointer">How long does delivery take?</summary>
            <p class="mt-3 text-gray-700">Delivery typically takes 5-7 business days depending on your location. Express shipping options are available at checkout.</p>
          </details>
          
          <details class="bg-white border border-green-200 rounded-lg p-4 group">
            <summary class="font-semibold text-green-800 cursor-pointer">Can I track my order?</summary>
            <p class="mt-3 text-gray-700">Yes! Once your order is shipped, you'll receive a tracking link via SMS and email to monitor your delivery status.</p>
          </details>
          
          <details class="bg-white border border-green-200 rounded-lg p-4 group">
            <summary class="font-semibold text-green-800 cursor-pointer">What is your return policy?</summary>
            <p class="mt-3 text-gray-700">We offer hassle-free returns within 7 days of delivery for most products. See our Refund Policy for details.</p>
          </details>
          
          <details class="bg-white border border-green-200 rounded-lg p-4 group">
            <summary class="font-semibold text-green-800 cursor-pointer">Are products authentic?</summary>
            <p class="mt-3 text-gray-700">Yes! All vendors on Nammahut are verified, and products are sourced directly from artisans and manufacturers.</p>
          </details>
        </div>
      </div>
    `
  },
  'privacy-policy': {
    title: 'Privacy Policy',
    content: `
      <div class="space-y-6">
        <p class="text-sm text-gray-500">Last updated: February 2025</p>
        
        <p>At Nammahut, we take your privacy seriously. This policy describes how we collect, use, and protect your personal information.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Information We Collect</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>Name, email, and phone number for account creation</li>
          <li>Shipping address for order delivery</li>
          <li>Payment information (processed securely by Cashfree)</li>
          <li>Browsing behavior and preferences</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">How We Use Your Information</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>Process and deliver your orders</li>
          <li>Send order updates and delivery notifications</li>
          <li>Improve our products and services</li>
          <li>Personalize your shopping experience</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Data Security</h3>
        <p>We use industry-standard encryption and security measures to protect your data. Payment information is never stored on our servers.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Contact Us</h3>
        <p>For privacy-related inquiries, contact us at privacy@nammahut.com</p>
      </div>
    `
  },
  'terms-of-service': {
    title: 'Terms of Service',
    content: `
      <div class="space-y-6">
        <p class="text-sm text-gray-500">Last updated: February 2025</p>
        
        <p>By using Nammahut, you agree to these terms of service. Please read them carefully.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Account Terms</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>You must provide accurate information when creating an account</li>
          <li>You are responsible for maintaining account security</li>
          <li>You must be 18+ years old to make purchases</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Orders & Payments</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>All prices are in Indian Rupees (INR)</li>
          <li>Prices may change without notice</li>
          <li>Orders are subject to product availability</li>
          <li>Payment must be completed to confirm orders</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Shipping</h3>
        <p>We ship across India. Delivery times vary by location. International shipping may be available for select products.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Intellectual Property</h3>
        <p>All content on Nammahut, including images and text, is protected by copyright law.</p>
      </div>
    `
  },
  'refund-policy': {
    title: 'Refund Policy',
    content: `
      <div class="space-y-6">
        <p class="text-lg">We want you to be completely satisfied with your purchase from Nammahut.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Returns</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>Returns accepted within 7 days of delivery</li>
          <li>Products must be unused and in original packaging</li>
          <li>Some items (food, perishables) cannot be returned</li>
          <li>Return shipping costs may apply</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Refund Process</h3>
        <ul class="list-disc pl-6 space-y-2">
          <li>Refunds processed within 5-7 business days</li>
          <li>Refund will be credited to original payment method</li>
          <li>Shipping charges are non-refundable</li>
        </ul>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">Damaged Items</h3>
        <p>If you receive a damaged product, contact us within 48 hours with photos. We'll arrange a replacement or refund at no extra cost.</p>
        
        <h3 class="text-xl font-semibold text-green-800 mt-6">How to Request a Return</h3>
        <ol class="list-decimal pl-6 space-y-2">
          <li>Email us at returns@nammahut.com with your order number</li>
          <li>Describe the reason for return</li>
          <li>We'll provide return instructions within 24 hours</li>
          <li>Ship the product back to us</li>
          <li>Refund processed after we receive the item</li>
        </ol>
      </div>
    `
  }
};

export default function ContentPage() {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Determine the slug from URL path or param
  const slug = paramSlug || PATH_TO_SLUG[location.pathname] || 'about-us';

  useEffect(() => {
    fetchContent();
  }, [slug]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/content/${slug}`);
      const data = response.data;
      
      // Handle different content formats
      if (data.content && typeof data.content === 'object') {
        // Legacy format - content is an object with sections
        const sections = [];
        if (data.content.hero) sections.push(`<p class="text-lg font-medium">${data.content.hero}</p>`);
        if (data.content.subtitle) sections.push(`<p class="text-gray-600">${data.content.subtitle}</p>`);
        if (data.content.sections) {
          data.content.sections.forEach(s => {
            sections.push(`<h3 class="text-xl font-semibold text-green-800 mt-6">${s.title || ''}</h3>`);
            sections.push(`<p>${s.content || ''}</p>`);
          });
        }
        setContent({
          title: data.title || data.page_name,
          content: sections.join('\n')
        });
      } else if (typeof data.content === 'string') {
        // New HTML format
        setContent(data);
      } else {
        // Use default
        setContent(DEFAULT_CONTENT[slug] || { title: 'Page', content: '' });
      }
    } catch (error) {
      // Use default content
      setContent(DEFAULT_CONTENT[slug] || { title: 'Page Not Found', content: '<p>This page does not exist.</p>' });
    } finally {
      setLoading(false);
    }
  };

  const IconComponent = PAGE_ICONS[slug] || FileText;

  if (loading) {
    return (
      <CustomerLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="min-h-screen bg-gradient-to-b from-[#FAFDF8] to-white">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-[#0F5132] via-[#198754] to-[#0F5132] py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-4">
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {content?.title || 'Page'}
            </h1>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg border border-[#0F5132]/10 p-8 md:p-12">
            <div 
              className="prose prose-lg max-w-none prose-headings:text-[#0F5132] prose-a:text-[#198754]"
              dangerouslySetInnerHTML={{ __html: content?.content || '' }}
            />
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
